import { observable } from 'mobx'
import { useEffect, useMemo, useState } from 'react'
import { BlobsClient } from '../../api/clients/blobs/BlobsClient'
import { WorkflowTaskResponse } from '../../api/clients/workflowTasks/DTOs'
import WorkflowTasksClient from '../../api/clients/workflowTasks/WorkflowTasksClient'
import { FullscreenSpinner } from '../../components/feedback/circular'
import { PickerDialog } from '../../components/pickers/PickerDialog'
import { ToastWrapper } from '../../components/toast/ToastWrapper'
import { WorkflowTaskResultValues } from '../../pages/WorkflowTasks/WorkflowTaskReviewPage'
import { toastService } from '../../services/notifications/ToastService'
import { EventBus } from '../FormBuilderCore/eventBus/EventBus'
import { StandaloneCellManager } from '../FormBuilderInterop/Runtimes/Standalone'
import { CheckOcrCorrectionPage } from './CheckOcrCorrectionPage'
import { DocumentReviewController } from './IDocumentReviewController'
import { OcrContextProvider } from './OcrContext'
import {
	OcrField,
	OcrLabelData,
	OcrMode,
	OcrResultFieldValue,
	OcrWorkflowTaskState,
} from './OcrTypes'

type OcrCorrectionPageWrapperProps = {
	onDocumentReviewed: (
		outcome: string,
		comparedValues: Record<string, WorkflowTaskResultValues>,
		obj: unknown,
	) => void
	isSubmitting: boolean
	workflowTaskId: number
	mode: OcrMode
}

type OcrResults = {
	fieldResults: Record<string, OcrResultFieldValue>
}

export const OcrCorrectionPageWrapper = (
	props: OcrCorrectionPageWrapperProps,
) => {
	const [ocrData, setOcrData] =
		useState<WorkflowTaskResponse<OcrWorkflowTaskState>>()
	const [document, setDocument] = useState<Blob>()
	const [isSubmitting, setIsSubmitting] = useState(false)

	useEffect(() => {
		fetchOcrData()
	}, [props.workflowTaskId])

	const [fieldReferences, fieldData]: [
		OcrResults,
		Record<string, OcrLabelData>,
	] = useMemo(() => {
		const fields = ocrData?.workflowTaskState.results.fields

		if (fields === undefined) return [{ fieldResults: {} }, {}]

		const innerFieldCollection: Record<string, OcrResultFieldValue> = {}
		const fieldData: Record<string, OcrLabelData> = {}

		function addToFieldCollection(
			fields: OcrField[],
			result: Record<string, unknown>,
		): void {
			for (let i = 0; i < fields.length; i++) {
				const field = fields[i]

				if (field.type === 'Value') {
					result[field.name] = field.value
					fieldData[field.name] = {
						boundingBox: field.value.boundingBox,
						labelText: '',
						pageNumber: field.value.pageNumber,
						hintText: field.value.hintText,
					}
				} else if (field.type === 'Repeat') {
					const listItems: Record<string, OcrResultFieldValue> = {}

					for (const item of field.value) {
						if (item.type === 'Value') {
							listItems[item.name] = item.value
							console.log('item value!!!', JSON.stringify(item.value, null, 2))

							fieldData[`${field.name}-${item.name}-${i}`] = {
								boundingBox: item.value.boundingBox,
								labelText: '',
								pageNumber: item.value.pageNumber,
								hintText: item.value.hintText,
							}
						}

						// TODO HANDLE NESTED LISTS!
					}

					let resultField = innerFieldCollection[field.name]
					let fieldDataField = fieldData[field.name]

					if (resultField === undefined) {
						resultField = {
							accepted: false,
							boundingBox: [],
							confidence: undefined,
							pageNumber: 0,
							value: [],
							children: [],
							valueType: 0,
						}
					}

					if (resultField.children === undefined) {
						resultField.children = []
						resultField.children.push(listItems)
					} else {
						resultField.children.push(listItems)
					}

					if (fieldDataField === undefined) {
						fieldDataField = {
							boundingBox: [],
							labelText: '',
							pageNumber: 0,
						}
					}

					innerFieldCollection[field.name] = resultField
				}
			}
		}

		addToFieldCollection(fields, innerFieldCollection)
		console.log('🎆', JSON.stringify(innerFieldCollection, null, 2))

		return [
			observable({ fieldResults: innerFieldCollection }),
			observable(fieldData),
		]
	}, [ocrData])

	const cellManager = useMemo(() => {
		if (ocrData === undefined)
			return new StandaloneCellManager({
				definitions: [],
			})

		console.log(
			'form builder schema',
			JSON.stringify(ocrData.formBuilderSchema, null, 2),
		)
		// css classes is in the form builder schema but somehow not being passed to the definitions

		return new StandaloneCellManager(
			ocrData.formBuilderSchema,
			fieldReferences,
			false,
			new EventBus(),
			// (cellManager) => ({
			// 	dialogService,
			// 	toastService,
			// 	getNamedInstance: (name: string) => {
			// 		for (const instance of cellManager.cellInstances)
			// 			if (instance.type !== CellType.Virtual)
			// 				if (instance.name === name) return instance
			// 	},
			// 	executeDataProvider: async (
			// 		instanceId: number,
			// 		parameters: Record<string, string>,
			// 	) => {
			// 		const dataProviderInstancesClient = new DataProviderInstancesClient()
			// 		const response = await dataProviderInstancesClient.execute(
			// 			instanceId,
			// 			parameters,
			// 		)
			// 		return response.data
			// 	},
			// }),
		)
	}, [ocrData])

	const onSubmit = async (
		outcome: string,
		originalValues: Record<string, unknown>,
		obj: unknown,
	) => {
		const comparedValues: Record<string, WorkflowTaskResultValues> = {}

		const ocrResults = obj as OcrResults

		// // TODO ADD THIS BACK!
		// for (const key of Object.keys(originalValues)) {
		// 	// only log the changed values

		// 	if (originalValues[key] !== ocrResults.fieldResults[key].value)
		// 		comparedValues[key] = {
		// 			originalValue: originalValues[key],
		// 			newValue: ocrResults.fieldResults[key].value,
		// 		}
		// }

		props.onDocumentReviewed(outcome, comparedValues, obj)
	}

	const fetchOcrData = async () => {
		const workflowTaskClient = new WorkflowTasksClient()
		const blobClient = new BlobsClient()

		try {
			const { data: workflowTask } = await workflowTaskClient.getWorkflowTask(
				props.workflowTaskId,
			)

			const ocrWorkflowTask =
				workflowTask as WorkflowTaskResponse<OcrWorkflowTaskState>

			console.log(
				'data from workflow task',
				JSON.stringify(ocrWorkflowTask.workflowTaskState.results, null, 2),
			)

			setOcrData(ocrWorkflowTask)

			const { data: blob } = await blobClient.getBlob(
				ocrWorkflowTask.workflowTaskState.documentBlobId,
			)

			console.log(blob)

			setDocument(blob)
		} catch (e) {
			console.error(e)
			toastService.displayToast({
				message: 'Error retrieving OCR data for review',
				area: 'global',
			})
		}
	}

	if (document === undefined || ocrData === undefined)
		return <FullscreenSpinner />

	return (
		<OcrContextProvider
			controller={
				new DocumentReviewController(
					props.workflowTaskId, // todo - this should be workflowinstanceid???
					cellManager,
					document,
					ocrData?.workflowTaskState,
					fieldData,
				)
			}
		>
			<CheckOcrCorrectionPage
				onSubmit={onSubmit}
				isSubmitting={isSubmitting}
				mode={props.mode}
			/>
			<PickerDialog />
			<ToastWrapper subscriptionArea="global" />
		</OcrContextProvider>
	)
}
